import React, { FC, ReactNode } from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  Button,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
} from '@material-ui/core'
import LiquidCheckbox from '@src/components/LiquidCheckbox'
import ModalSkeleton from './ModalSkeleton'
import HandleMarkup from '@src/components/HandleMarkup'
import { Link } from '@src/components/Link'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '@utils/messages'
import { SavedCart } from '@src/types/graphql-types'
import { savedCartRoute } from '@src/routes'
import { useQuickOrder } from '@src/components/QuickOrder/utils/quickOrderContext'
import { MODALTYPE } from './enum'
import { useSavedCart } from '@utils/useCart'
import { sendCommonDetailClickEvent } from '@src/utils/analytics'
import {
  DejaNewEventCategory as eventCategory,
  DejaNewEventAction as eventAction,
  DejaNewEventLabel as eventLabel,
} from '@utils/analytics/enums'
import { lineClamp } from '@src/styles/utils/lineClamp'
import clsx from 'clsx'

const useStyles = makeStyles((theme: Theme) => {
  return {
    skuCell: {
      alignItems: 'center',
      whiteSpace: 'nowrap',
      padding: theme.spacing(0, 0, 0, 1),
      '& > label': {
        alignItems: 'center',
        verticalAlign: 'middle',
      },
      '& > label > span': {
        marginTop: 0,
        lineHeight: 1.46,
      },
    },
    description: {
      ...lineClamp(2),
    },
    tableContainer: {
      maxHeight: '14rem',
      marginBottom: theme.spacing(2),
    },
    tableHeadCell: {
      paddingLeft: theme.spacing(7),
      width: '5rem',
    },
    tableHeadRow: {
      whiteSpace: 'nowrap',
      fontSize: theme.typography.pxToRem(14),
    },
    tableRow: {
      '& .MuiTableCell-body': {
        border: 0,
        verticalAlign: 'baseline',
      },
    },
    quantity: {
      display: 'block',
      textAlign: 'center',
    },
    checkboxSpacingAdjustment: {
      position: 'relative',
      top: theme.spacing(0.75),
    },
  }
})

const SavedCartsModalTitle: FC<{ savedCart: SavedCart }> = ({ savedCart }) => {
  const intl = useIntl()

  return (
    <>
      {intl.formatMessage({ id: 'SAVED_CART', defaultMessage: 'Saved Cart' })}:{' '}
      {savedCart.cartName} -{' '}
      {intl.formatDate(new Date(savedCart.savedDate), {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })}
    </>
  )
}

const ItemRow: FC<{
  index: number
  materialId: string
  quantity: number
  description: string
}> = ({ index, materialId, quantity, description }) => {
  const classes = useStyles()
  const { pendingItems, updatePendingItems } = useQuickOrder()

  const isChecked = pendingItems.some((item) => item.id === index)

  const handleCheck = () => {
    updatePendingItems(index, materialId, quantity)
  }

  return (
    <>
      <TableRow classes={{ root: classes.tableRow }}>
        <TableCell className={classes.skuCell}>
          <LiquidCheckbox
            name={`${materialId}-${index}`}
            checked={isChecked}
            onChange={() => handleCheck()}
            label={
              <Typography variant="body2" component="span">
                {materialId}
              </Typography>
            }
          />
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            component="span"
            className={clsx(
              classes.quantity,
              classes.checkboxSpacingAdjustment
            )}
          >
            {quantity}
          </Typography>
        </TableCell>
        <TableCell>
          <HandleMarkup
            className={clsx(
              classes.description,
              classes.checkboxSpacingAdjustment
            )}
            value={description}
          />
        </TableCell>
      </TableRow>
    </>
  )
}

const SavedCartsItemsTable: FC<{ children: ReactNode }> = ({ children }) => {
  const classes = useStyles()

  return (
    <>
      <TableContainer className={classes.tableContainer}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow className={classes.tableHeadRow}>
              <TableCell classes={{ root: classes.tableHeadCell }}>
                <FormattedMessage {...messages.SKU} />
              </TableCell>
              <TableCell>
                <FormattedMessage {...messages.ABBREVIATED_QUANTITY} />
              </TableCell>
              <TableCell>
                <FormattedMessage {...messages.DESCRIPTION} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

const getAnalyticsModalTitle = (intl, savedCartName, date) => {
  return (
    `${intl.formatMessage({
      id: 'SAVED_CART',
      defaultMessage: 'Saved Cart',
    })}: ${savedCartName} - ${intl.formatDate(new Date(date), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })}` || ''
  )
}

const SavedCartsModalContent: FC<{
  savedCart: SavedCart
  items: any
  detailTitleForAnalytics: string
}> = ({ savedCart, items, detailTitleForAnalytics }) => {
  const { cartId } = savedCart
  return (
    <>
      <SavedCartsItemsTable>
        {items.map((item, index) => {
          return (
            <ItemRow
              key={index}
              index={index}
              materialId={item.materialId || ''}
              quantity={item.quantity}
              description={item.materialDescription}
            />
          )
        })}
      </SavedCartsItemsTable>
      <Link {...savedCartRoute.index(cartId)}>
        {/* jsx-a11y rules disabled because href is implicitly passed to anchor */}
        {}
        <a
          onClick={() => {
            sendCommonDetailClickEvent(
              {
                event: 'quick_order_widget_interaction',
                action: 'visit saved cart details',
                detail: detailTitleForAnalytics,
                section: 'add to quick order',
                component: 'modal',
                elementType: 'link',
                elementText: 'go to saved carts',
                linkUrl: `${savedCartRoute.index(cartId).as}`,
                coreEvent: 'no',
              },
              {
                eventCategory: eventCategory.QuickOrderPage,
                eventAction: eventAction.SavedCarts,
                eventLabel: eventLabel.ModalGoToSavedCarts,
                eventInteractionType: 0,
              }
            )
          }}
        >
          <FormattedMessage {...messages.GO_TO_SAVED_CART} />
          {' >'}
        </a>
      </Link>
    </>
  )
}

const SavedCartsModalActions: FC<{
  onClose: () => void
  items: any
  detailTitleForAnalytics: string
}> = ({ onClose, items, detailTitleForAnalytics }) => {
  const { pendingItems, setAddItems, setPendingItems } = useQuickOrder()

  const itemsSelected = pendingItems.length > 0

  const handleAddSelectedClick = () => {
    sendCommonDetailClickEvent(
      {
        event: 'quick_order_widget_interaction',
        action: 'add selected saved cart items',
        detail: detailTitleForAnalytics,
        section: 'add to quick order',
        component: 'modal',
        elementType: 'button',
        elementText: 'add selected',
        coreEvent: 'no',
      },
      {
        eventCategory: eventCategory.QuickOrderPage,
        eventAction: eventAction.SavedCarts,
        eventLabel: eventLabel.ModalAddSelected,
        eventInteractionType: 0,
      }
    )
    setAddItems(true)
    onClose()
  }

  const handleAddAllClick = () => {
    sendCommonDetailClickEvent(
      {
        event: 'quick_order_widget_interaction',
        action: 'add all saved cart items',
        detail: detailTitleForAnalytics,
        section: 'add to quick order',
        component: 'modal',
        elementType: 'button',
        elementText: 'add all',
        coreEvent: 'no',
      },
      {
        eventCategory: eventCategory.QuickOrderPage,
        eventAction: eventAction.SavedCarts,
        eventLabel: eventLabel.ModalAddAll,
        eventInteractionType: 0,
      }
    )
    setPendingItems(
      items
        ? items.map((item, index) => {
            return {
              id: index,
              materialNumber: item.materialId,
              quantity: item.quantity,
            }
          })
        : []
    )
    setAddItems(true)
    onClose()
  }

  return (
    <>
      <Button
        color="primary"
        disabled={!itemsSelected}
        onClick={() => handleAddSelectedClick()}
        size="large"
        variant="outlined"
      >
        <FormattedMessage {...messages.ADD_SELECTED} />
      </Button>
      <Button
        color="primary"
        onClick={() => handleAddAllClick()}
        size="large"
        variant="contained"
      >
        <FormattedMessage {...messages.ADD_ALL} />
      </Button>
    </>
  )
}

const SavedCartsModal: FC<{
  onClose: () => void
  open: boolean
  savedCart: SavedCart
}> = ({ onClose, open, savedCart }) => {
  const intl = useIntl()
  const { cartId } = savedCart
  const { data, loading } = useSavedCart({
    variables: { cartId },
    fetchPolicy: 'no-cache',
  })

  if (loading) return null

  const detailTitleForAnalytics = getAnalyticsModalTitle(
    intl,
    savedCart.cartName,
    savedCart.savedDate
  )

  return (
    <ModalSkeleton
      type={MODALTYPE.SavedCartsModal}
      actions={
        <SavedCartsModalActions
          items={data?.getSavedCart.items}
          onClose={() => onClose()}
          detailTitleForAnalytics={detailTitleForAnalytics}
        />
      }
      content={
        <SavedCartsModalContent
          savedCart={savedCart}
          items={data?.getSavedCart.items}
          detailTitleForAnalytics={detailTitleForAnalytics}
        />
      }
      onClose={() => onClose()}
      open={open}
      title={<SavedCartsModalTitle savedCart={savedCart} />}
    />
  )
}

export default SavedCartsModal
